import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';

import {
  AdminSectionCard,
  ConfirmDialog,
  ConfirmSwitch,
  HelpAnchor,
  InlineError,
  Pill,
  SkeletonBlock,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { ROW_DIVIDERS } from '@/shared/ui-class';
import { SEED_BUILTIN_TOOLS } from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import { APP_MCP_SUBFAMILY_BOUNDARY_NOTES } from './builtin-tools-panel-helpers';
import {
  ADMIN_PANEL_BUILTIN_TOOL_NAMES,
  buildPersistPayload,
  categoriesInRenderOrder,
  CORE_FAMILY_IMPACT,
  CORE_PLATFORM_FAMILY_LABELS,
  CORE_PLATFORM_MCP_FAMILIES,
  KNOWN_BUILTIN_TOOLS,
  PLATFORM_FLOOR_SAFETY_TOOLS,
  presetImpactCopy,
  ROW_COPY,
  tierForTool,
  type AppMcpSubfamilyId,
  type BuiltinToolsPersistPayload,
  type CoreFamilyId,
  type FloorTier,
  type PlatformToolCategory,
} from './BuiltinToolsScene.transform';
const SHARED_FOOTER = (
  <p className="mt-3 text-xs text-muted">
    <strong>Always-on is the supported configuration.</strong> This is an
    emergency escape hatch — don&apos;t do this unless you must, and re-enable
    as soon as the incident is over. It applies to{" "}
    <strong>every user on this deployment</strong> and takes effect on each
    user&apos;s next message (config cache ≤ 30 s).
  </p>
);

function familyImpactContent(familyId: CoreFamilyId): ReactNode {
  const impact = CORE_FAMILY_IMPACT[familyId];
  return (
    <div>
      <p className="text-sm text-zinc-700 dark:text-zinc-300">
        {impact.summary}
      </p>
      <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted">
        What breaks
      </p>
      <ul className="mt-1 list-disc space-y-1 pl-4 text-sm text-muted">
        {impact.breaks.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {SHARED_FOOTER}
    </div>
  );
}

function DangerImpactConfirm(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  impact: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
}) {
  return (
    <ConfirmDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title={props.title}
      description={props.impact}
      confirmLabel={props.confirmLabel}
      tone="danger"
      onConfirm={() => {
        props.onConfirm();
      }}
    />
  );
}

function TierControl({
  toolName,
  tier,
  onChange,
  disabled,
}: {
  toolName: string;
  tier: FloorTier;
  onChange: (next: FloorTier) => void;
  disabled?: boolean;
}) {
  const options: { value: FloorTier; label: string }[] = [
    { value: "alwaysOn", label: "Always on" },
    { value: "default", label: "Default" },
    { value: "alwaysOff", label: "Platform deny" },
  ];
  return (
    <div
      className="inline-flex rounded-lg border border-zinc-200 p-0.5 dark:border-zinc-800"
      role="radiogroup"
      aria-label={`Policy for ${toolName}`}
    >
      {options.map((opt) => {
        const isActive = tier === opt.value;
        const isDenyActive = opt.value === "alwaysOff" && isActive;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              isActive
                ? isDenyActive
                  ? "bg-red-600 text-white dark:bg-red-500"
                  : "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                : "text-zinc-600 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-900",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function SectionRowSkeleton() {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="min-w-0 flex-1 space-y-2">
        <SkeletonBlock className="h-4 w-40" />
        <SkeletonBlock className="h-3 w-24" />
        <SkeletonBlock className="h-3 w-full max-w-md" />
      </div>
      <SkeletonBlock className="h-5 w-9 shrink-0 rounded-full" />
    </div>
  );
}

function subfamilyImpactContent(category: PlatformToolCategory): ReactNode {
  const boundary = APP_MCP_SUBFAMILY_BOUNDARY_NOTES[category.id as AppMcpSubfamilyId];
  return (
    <div>
      <p className="text-sm text-zinc-700 dark:text-zinc-300">
        {category.description}
      </p>
      {boundary ? (
        <p className="mt-2 text-sm text-muted">{boundary}</p>
      ) : null}
      {SHARED_FOOTER}
    </div>
  );
}

export function AppMcpSubfamiliesGroup({
  subfamiliesDisabled,
  subfamilyToolCounts,
  masterOff,
  saving,
  expanded,
  onExpandedChange,
  onEnable,
  onDisable,
}: {
  subfamiliesDisabled: Set<AppMcpSubfamilyId>;
  /** F29 — server-computed per-category counts; absent on stale servers. */
  subfamilyToolCounts?: Record<string, number>;
  masterOff: boolean;
  saving: boolean;
  expanded: boolean;
  onExpandedChange: (open: boolean) => void;
  onEnable: (id: AppMcpSubfamilyId) => void;
  onDisable: (id: AppMcpSubfamilyId) => void;
}) {
  const categories = categoriesInRenderOrder();

  return (
    <div className="mt-2 rounded-lg border border-divider bg-zinc-50/80 dark:bg-zinc-900/40">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => onExpandedChange(!expanded)}
        className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-100/80 dark:text-zinc-300 dark:hover:bg-zinc-900/60"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        )}
        App MCP sub-families
        <span className="text-xs font-normal text-muted">
          ({categories.length})
        </span>
      </button>

      {expanded ? (
        <div
          className={cn(
            ROW_DIVIDERS,
            "border-t border-divider px-3",
            masterOff && "opacity-60",
          )}
          aria-disabled={masterOff}
        >
          {categories.map((category) => {
            const isDisabled = subfamiliesDisabled.has(
              category.id as AppMcpSubfamilyId,
            );
            const boundary =
              APP_MCP_SUBFAMILY_BOUNDARY_NOTES[
                category.id as AppMcpSubfamilyId
              ];
            const toolCount = subfamilyToolCounts?.[category.id];
            const toolCountSuffix =
              toolCount !== undefined ? (
                <span className="font-normal text-muted">
                  {" · "}
                  {toolCount} {toolCount === 1 ? "tool" : "tools"} (approx.)
                </span>
              ) : null;
            return (
              <div
                key={category.id}
                className="flex items-start justify-between gap-4 py-3 first:pt-3 last:pb-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "text-sm font-medium",
                        isDisabled
                          ? "text-red-600 dark:text-red-400"
                          : "text-zinc-900 dark:text-zinc-100",
                      )}
                    >
                      {category.name}
                      {toolCountSuffix}
                    </span>
                    {isDisabled ? (
                      <Pill tone="danger">Disabled</Pill>
                    ) : null}
                  </div>
                  <code className="mt-0.5 block text-xs text-muted">
                    {category.id}
                  </code>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted">
                    {category.description}
                  </p>
                  {boundary ? (
                    <p className="mt-1 text-xs leading-relaxed text-muted">
                      {boundary}
                    </p>
                  ) : null}
                </div>
                <div className="mt-0.5 shrink-0">
                  <ConfirmSwitch
                    checked={!isDisabled}
                    guardWhen="off"
                    confirmation={{ expected: category.name }}
                    confirm={{
                      title: `Disable ${category.name} for every user?`,
                      description: subfamilyImpactContent(category),
                      confirmLabel: `Disable ${category.name}`,
                      tone: "danger",
                    }}
                    onCheckedChange={() =>
                      onEnable(category.id as AppMcpSubfamilyId)
                    }
                    onConfirm={() =>
                      onDisable(category.id as AppMcpSubfamilyId)
                    }
                    saving={saving}
                    disabled={masterOff || saving}
                    aria-label={`Toggle ${category.name}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function BuiltinToolsPanelSkeleton() {
  return (
    <div className="space-y-6" aria-hidden>
      <AdminSectionCard>
        <SkeletonBlock className="h-4 w-44" />
        <SkeletonBlock className="mt-2 h-3 w-full max-w-xl" />
        <div className={cn(ROW_DIVIDERS, "mt-4")}>
          {Array.from({ length: 4 }).map((_, i) => (
            <SectionRowSkeleton key={i} />
          ))}
        </div>
      </AdminSectionCard>
      <AdminSectionCard>
        <SkeletonBlock className="h-4 w-64" />
        <SkeletonBlock className="mt-2 h-3 w-full max-w-lg" />
        <div className={cn(ROW_DIVIDERS, "mt-4")}>
          {Array.from({ length: 6 }).map((_, i) => (
            <SectionRowSkeleton key={i} />
          ))}
        </div>
      </AdminSectionCard>
    </div>
  );
}

function BuiltinToolsSceneInner() {
  const [alwaysOn, setAlwaysOn] = useState(
    () => new Set(SEED_BUILTIN_TOOLS.alwaysOn),
  );
  const [alwaysOff, setAlwaysOff] = useState(
    () => new Set(SEED_BUILTIN_TOOLS.alwaysOff),
  );
  const [coreFamiliesDisabled, setCoreFamiliesDisabled] = useState(
    () => new Set(SEED_BUILTIN_TOOLS.coreFamiliesDisabled as CoreFamilyId[]),
  );
  const [appMcpSubfamiliesDisabled, setAppMcpSubfamiliesDisabled] = useState(
    () =>
      new Set(
        SEED_BUILTIN_TOOLS.appMcpSubfamiliesDisabled as AppMcpSubfamilyId[],
      ),
  );
  const [appMcpSubsExpanded, setAppMcpSubsExpanded] = useState(false);
  const [appMcpSubfamilyToolCounts, setAppMcpSubfamilyToolCounts] = useState<
    Record<string, number> | undefined
  >(() => SEED_BUILTIN_TOOLS.appMcpSubfamilyToolCounts);
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [denyModalTarget, setDenyModalTarget] = useState<string | null>(null);

  const loadConfig = useCallback(() => {
    setError(null);
    const json = SEED_BUILTIN_TOOLS;
    setAlwaysOn(new Set(json.alwaysOn));
    setAlwaysOff(new Set(json.alwaysOff));
    setCoreFamiliesDisabled(
      new Set(json.coreFamiliesDisabled as CoreFamilyId[]),
    );
    setAppMcpSubfamiliesDisabled(
      new Set(json.appMcpSubfamiliesDisabled as AppMcpSubfamilyId[]),
    );
    setAppMcpSubfamilyToolCounts(json.appMcpSubfamilyToolCounts);
  }, []);

  void loadConfig;

  const persistConfig = useCallback(async (_payload: BuiltinToolsPersistPayload) => {
    setSaving(true);
    setSaveError(null);
    setSaving(false);
  }, []);

  const applyTierAndPersist = async (toolName: string, tier: FloorTier) => {
    const nextOn = new Set(alwaysOn);
    const nextOff = new Set(alwaysOff);
    nextOn.delete(toolName);
    nextOff.delete(toolName);
    if (tier === "alwaysOn") nextOn.add(toolName);
    if (tier === "alwaysOff") nextOff.add(toolName);
    setAlwaysOn(nextOn);
    setAlwaysOff(nextOff);
    await persistConfig(
      buildPersistPayload({
        alwaysOn: nextOn,
        alwaysOff: nextOff,
        coreFamiliesDisabled,
        appMcpSubfamiliesDisabled,
      }),
    );
  };

  const enableFamily = async (familyId: CoreFamilyId) => {
    const nextFamilies = new Set(coreFamiliesDisabled);
    nextFamilies.delete(familyId);
    setCoreFamiliesDisabled(nextFamilies);
    await persistConfig(
      buildPersistPayload({
        alwaysOn,
        alwaysOff,
        coreFamiliesDisabled: nextFamilies,
        appMcpSubfamiliesDisabled,
      }),
    );
  };

  const disableFamily = async (familyId: CoreFamilyId) => {
    const nextFamilies = new Set(coreFamiliesDisabled);
    nextFamilies.add(familyId);
    setCoreFamiliesDisabled(nextFamilies);
    await persistConfig(
      buildPersistPayload({
        alwaysOn,
        alwaysOff,
        coreFamiliesDisabled: nextFamilies,
        appMcpSubfamiliesDisabled,
      }),
    );
  };

  const masterAppMcpOff = coreFamiliesDisabled.has("app_mcp");

  const enableSubfamily = async (subfamilyId: AppMcpSubfamilyId) => {
    if (masterAppMcpOff) return;
    const nextSubs = new Set(appMcpSubfamiliesDisabled);
    nextSubs.delete(subfamilyId);
    setAppMcpSubfamiliesDisabled(nextSubs);
    await persistConfig(
      buildPersistPayload({
        alwaysOn,
        alwaysOff,
        coreFamiliesDisabled,
        appMcpSubfamiliesDisabled: nextSubs,
      }),
    );
  };

  const disableSubfamily = async (subfamilyId: AppMcpSubfamilyId) => {
    if (masterAppMcpOff) return;
    const nextSubs = new Set(appMcpSubfamiliesDisabled);
    nextSubs.add(subfamilyId);
    setAppMcpSubfamiliesDisabled(nextSubs);
    await persistConfig(
      buildPersistPayload({
        alwaysOn,
        alwaysOff,
        coreFamiliesDisabled,
        appMcpSubfamiliesDisabled: nextSubs,
      }),
    );
  };

  const onTierChange = (toolName: string, next: FloorTier) => {
    const current = tierForTool(toolName, alwaysOn, alwaysOff);
    if (next === "alwaysOff" && current !== "alwaysOff") {
      setDenyModalTarget(toolName);
      setDenyModalOpen(true);
    } else {
      void applyTierAndPersist(toolName, next);
    }
  };

  const sdkPresetNames = ADMIN_PANEL_BUILTIN_TOOL_NAMES.filter((n) =>
    (KNOWN_BUILTIN_TOOLS as readonly string[]).includes(n),
  );
  const sdkExtraNames = ADMIN_PANEL_BUILTIN_TOOL_NAMES.filter(
    (n) => !(KNOWN_BUILTIN_TOOLS as readonly string[]).includes(n),
  );

  return (
    <div className="space-y-6">
      {error && <InlineError>{error}</InlineError>}

      {loading ? (
        <BuiltinToolsPanelSkeleton />
      ) : (
        <>
          <AdminSectionCard
            title="Core platform tools"
            description="First-party platform tools — the agent's CRUD over your memories, artifacts, and project files. These are always on by default and should stay on — the switches are an emergency escape hatch for a misbehaving family, not a configuration surface."
          >
            <div className={ROW_DIVIDERS}>
              {CORE_PLATFORM_MCP_FAMILIES.map((familyId) => {
                const label = CORE_PLATFORM_FAMILY_LABELS[familyId];
                const isDisabled = coreFamiliesDisabled.has(familyId);
                const impact = CORE_FAMILY_IMPACT[familyId];
                return (
                  <div key={familyId}>
                    <div className="flex items-center justify-between gap-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={cn(
                              "text-sm font-medium",
                              isDisabled
                                ? "text-red-600 dark:text-red-400"
                                : "text-zinc-900 dark:text-zinc-100",
                            )}
                          >
                            {label}
                          </span>
                          {isDisabled ? (
                            <Pill tone="danger">
                              Disabled — emergency override
                            </Pill>
                          ) : null}
                        </div>
                        <code className="text-xs text-muted">{familyId}</code>
                        {isDisabled ? (
                          <p className="mt-1 text-sm text-muted">
                            {impact.summary}
                          </p>
                        ) : null}
                      </div>
                      <ConfirmSwitch
                        checked={!isDisabled}
                        guardWhen="off"
                        confirmation={{ expected: label }}
                        confirm={{
                          title: `Disable ${label} for every user?`,
                          description: familyImpactContent(familyId),
                          confirmLabel: `Disable ${label}`,
                          tone: "danger",
                        }}
                        onCheckedChange={() => void enableFamily(familyId)}
                        onConfirm={() => void disableFamily(familyId)}
                        saving={saving}
                        aria-label={`Toggle ${label}`}
                      />
                    </div>
                    {familyId === "app_mcp" ? (
                      <AppMcpSubfamiliesGroup
                        subfamiliesDisabled={appMcpSubfamiliesDisabled}
                        subfamilyToolCounts={appMcpSubfamilyToolCounts}
                        masterOff={masterAppMcpOff}
                        saving={saving}
                        expanded={appMcpSubsExpanded}
                        onExpandedChange={setAppMcpSubsExpanded}
                        onEnable={(id) => void enableSubfamily(id)}
                        onDisable={(id) => void disableSubfamily(id)}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </AdminSectionCard>

          <AdminSectionCard
            title="Built-in SDK preset — Claude Agent SDK"
            description="The Claude Agent SDK's native tools the agent uses to operate in its sandbox."
          >
            <div className={ROW_DIVIDERS}>
              {[...sdkPresetNames, ...sdkExtraNames].map((toolName) => {
                const copy = ROW_COPY[toolName] ?? {
                  label: toolName,
                  description: "",
                };
                const isSafety = (
                  PLATFORM_FLOOR_SAFETY_TOOLS as readonly string[]
                ).includes(toolName);
                const currentTier = tierForTool(toolName, alwaysOn, alwaysOff);
                const isDenied = currentTier === "alwaysOff";
                return (
                  <div
                    key={toolName}
                    className="flex flex-wrap items-start justify-between gap-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-zinc-900 dark:text-zinc-100">
                        {copy.label}
                        <span className="ml-2 text-xs font-normal text-muted">
                          {toolName}
                        </span>
                        {isSafety ? (
                          <span className="ml-2 text-xs text-muted">
                            (safety — survives composer built-ins off)
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-sm text-muted">
                        {copy.description}
                      </p>
                      {isDenied ? (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          Denied platform-wide — no user or thread setting can
                          re-enable it
                        </p>
                      ) : null}
                    </div>
                    <TierControl
                      toolName={toolName}
                      tier={currentTier}
                      onChange={(t) => onTierChange(toolName, t)}
                      disabled={saving}
                    />
                  </div>
                );
              })}
            </div>
          </AdminSectionCard>

          {denyModalTarget ? (
            <DangerImpactConfirm
              open={denyModalOpen}
              onOpenChange={(open) => {
                setDenyModalOpen(open);
                if (!open) setDenyModalTarget(null);
              }}
              title={`Deny ${ROW_COPY[denyModalTarget]?.label ?? denyModalTarget} platform-wide?`}
              confirmLabel="Deny platform-wide"
              impact={
                <div>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300">
                    {presetImpactCopy(denyModalTarget)}
                  </p>
                  {ROW_COPY[denyModalTarget]?.description ? (
                    <p className="mt-2 text-sm text-muted">
                      {ROW_COPY[denyModalTarget].description}
                    </p>
                  ) : null}
                  {SHARED_FOOTER}
                </div>
              }
              onConfirm={() => {
                const target = denyModalTarget;
                setDenyModalOpen(false);
                setDenyModalTarget(null);
                if (target) void applyTierAndPersist(target, "alwaysOff");
              }}
            />
          ) : null}

          {saveError && <InlineError>{saveError}</InlineError>}
        </>
      )}
    </div>
  );
}

export function BuiltinToolsScene() {
  return (
    <AdminSceneShell
      sceneName="BuiltinToolsScene"
      title="Built-in tools"
      description="Platform tool floors and core MCP families."
      headerActions={<HelpAnchor doc="workspace/admin" />}
    >
      <Commentable name="Panel">
        <BuiltinToolsSceneInner />
      </Commentable>
    </AdminSceneShell>
  );
}
